/**
 * Identifier generation. Isomorphic — uses WebCrypto, available in Node 20+
 * and every browser we target.
 */

const webcrypto = globalThis.crypto;

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  webcrypto.getRandomValues(bytes);
  return bytes;
}

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

/**
 * UUIDv7 — time-ordered, so primary keys stay index-friendly under Postgres
 * B-trees instead of scattering writes like v4 does.
 * Layout: 48-bit big-endian ms timestamp | version | 12 random | variant | 62 random.
 */
export function uuidv7(now: number = Date.now()): string {
  const bytes = randomBytes(16);
  const ms = BigInt(now);

  bytes[0] = Number((ms >> 40n) & 0xffn);
  bytes[1] = Number((ms >> 32n) & 0xffn);
  bytes[2] = Number((ms >> 24n) & 0xffn);
  bytes[3] = Number((ms >> 16n) & 0xffn);
  bytes[4] = Number((ms >> 8n) & 0xffn);
  bytes[5] = Number(ms & 0xffn);

  bytes[6] = (bytes[6]! & 0x0f) | 0x70; // version 7
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // RFC 4122 variant

  const h = (i: number) => HEX[bytes[i]!]!;
  return (
    h(0) + h(1) + h(2) + h(3) + '-' +
    h(4) + h(5) + '-' +
    h(6) + h(7) + '-' +
    h(8) + h(9) + '-' +
    h(10) + h(11) + h(12) + h(13) + h(14) + h(15)
  );
}

/**
 * Crockford base32 without I, L, O, U — a merchant reads these aloud over the
 * phone and types them into a search box, so ambiguity is a real cost.
 */
const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

export function shortCode(length = 6): string {
  const bytes = randomBytes(length);
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += CODE_ALPHABET[bytes[i]! % CODE_ALPHABET.length];
  }
  return out;
}

/**
 * Order codes look like "A7K2QP" — no prefix, because they are always shown
 * next to the word "Order"/"ការបញ្ជាទិញ" and merchants type them into search.
 */
export function orderCode(): string {
  return shortCode(6);
}

/** URL-safe opaque token for staff invites, webhook secrets, deep links. */
export function token(byteLength = 24): string {
  const bytes = randomBytes(byteLength);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  const base64 = typeof btoa === 'function' ? btoa(binary) : Buffer.from(bytes).toString('base64');
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Stable SKU suffix for auto-generated retail variants. */
export function skuSuffix(): string {
  return shortCode(4);
}
